// @TODO: rename...this is only used in goals and assignments...so it's kind of a passage but not really (passagedisplay is an _actual_ passage)
/**
 * Ryan O'Dowd
 * 2022-03-15
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useMemo,
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import CPassage from '@classes/Passage';
import {
  IconButton,
} from 'react-native-paper';
import LinearProgress from '@components/common/LinearProgress';
import ListItem from '@components/ListItem';
import {
  PRACTICE_ROUTE,
} from '@utils/store/actions';
import PassageDisplay from '@components/PassageDisplay';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const Passage = (props: PassageProps) => {
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  const [_isSelectionModeOn, setIsSelectionModeOn] = useState(false);

  const settingsPreferredVerseSelectionMethod = hooks.useTypedSelector((state) => state.settingsPreferredVerseSelectionMethod);

  const wrinkles = useMemo(() => {
    return props.passage.getWrinkles();
  }, [props.passage]); // @TODO: is this memo working, or is the reference for props.passage different on every render? // @TODO: need to return passage and wrinkles together because calling .getWrinkles() can return a different value...this means that when leveling up, it's possible (probable/guaranteed) that the UI will not update to reflect the new level achieved without a re-render being triggered

  const passageVerses = hooks.useGetVersesForPassages([props.passage])[props.passage.id] || []; // @TODO: this doesn't get recalculated when Bible is deleted locally (edge case, but something to consider)

  const isTestOpen = !!wrinkles.length && wrinkles.some((wrinkle) => wrinkle.level_number >= 1 && (new Date(wrinkle.date_next_srs_level).getTime() < Date.now())); // @TODO: abstract to a method on a Wrinkle class
  const isPassageCompleted = !props.hideProgressStyling && (wrinkles.length === passageVerses.length) && wrinkles.every((wrinkle) => (wrinkle.level_number || 0) >= globals.srsLevelWrinkleIsEarnedAt); // comparing wrinkles.length and passageVerses.length so that previews don't look completed (e.g. if Genesis 1:1-5 is the passage, and there's a wrinkle for 1:1, but 1:2-5 aren't in any current goals, then every wrinkle in redux is completed (but 2-5 are missing since they're not unstarted)

  let passageWrinkleLevelNumber = 0; // @TODO: this is too hacky; 0 means no level ups, -1 means there are more than one ready but at different levels
  let hasVersesReadyForLevelUp = false;
  wrinkles.forEach((w) => {
    if (!w.is_paused && w.level_number > 0 && (Date.now() >= new Date(w.date_next_srs_level).getTime())) {
      hasVersesReadyForLevelUp = true;
      if (passageWrinkleLevelNumber !== -1) { // @TODO: i don't like using -1
        if (passageWrinkleLevelNumber && (w.level_number !== passageWrinkleLevelNumber)) {
          passageWrinkleLevelNumber = -1;
        } else {
          passageWrinkleLevelNumber = w.level_number;
        }
      }
    }
  });

  return (
    <ListItem
      onPress={() => {
        setIsSelectionModeOn(false);
      }}
      variant={isPassageCompleted ? 'accent' : 'primary'}
      isExpanded={props.isExpanded}
      accordionContent={
        <TouchableOpacity
          style={styles.versesWrapper}
          disabled={true} // to prevent presses from activating the list item onPress
        >
          <PassageDisplay
            key={props.passage.id}
            passages={[props.passage]}
            passageForContext={props.passage}
            hideLicensing={true}
            showFormatting={false}
            showFootnotes={false}
            showHeadings={props.showHeadings}
            hideCompare={true}
            hideAddToGoal={true}
            verseNumbersOnly={settingsPreferredVerseSelectionMethod === 'numbers'}
            alwaysShowSelectionMenu={!props.hideSelectionMenu}
            bottomBorderColorOverride={isPassageCompleted ? styles.colors.accent : null}
            showWrinkleBadges={!props.hideWrinkleBadges}
            onSelectionModeToggle={(isOn) => setIsSelectionModeOn(isOn)}
            assignmentId={props.assignmentId}
            spritzPassage={props.spritzPassage}
            spritzGoToPreviousPassage={props.spritzGoToPreviousPassage}
            spritzGoToNextPassage={props.spritzGoToNextPassage}
            hideProgressStyling={props.hideProgressStyling}
            isPressDisabled={props.isPressDisabled}
          />
        </TouchableOpacity>
      }
    >
      <View style={styles.row}>
        <View style={styles.headerWrapper}>
          <View style={styles.chapterMetadataWrapper}>
            <View style={styles.firstRow}>
              <Text style={styles.passageTitle}>{props.passage.prettyPassageName(props.includeTranslationAbbreviation)}</Text>
            </View>
          </View>
          {(props.normalizedScore !== undefined) &&
            <View style={[styles.normalizedScoreWrapper, isPassageCompleted && styles.completedNormalizedScoreWrapper, !props.normalizedScore && styles.zeroNormalizedScoreWrapper]}>
              {props.normalizedScore
                ? (
                  <LinearProgress
                    style={styles.normalizedScoreGradient}
                    color={props.normalizedScore >= 1 ? styles.colors.accent : styles.colors.white}
                    fillStyle={props.normalizedScore >= 1 ? styles.normalizedScoreFillCompleted : styles.normalizedScoreFill(props.normalizedScore * 100)}
                    progress={props.normalizedScore}
                  />
                ) : (
                  <View style={styles.normalizedScoreGradient}>
                    <View style={styles.emptyScore} />
                  </View>
                )
              }
              {/* @TODO: probably good to show some kind of progress of peanuts...maybe this is the normalized score? or just a number value? something that shows which passages have even been started? */}
            </View>
          }
        </View>
        {!props.hideActions &&
          <View style={styles.rightActions}>
            {!props.hideTestIcon &&
              <View>
                <IconButton
                  style={[
                    styles.passageActionButton,
                    // @TODO: !isTestOpen && styles.closedTestIcon,
                    (!!wrinkles.length && wrinkles.every((wrinkle) => (wrinkle.level_number || 0) >= globals.srsLevelWrinkleIsEarnedAt)) && styles.levelFiveButton,
                    _isSelectionModeOn && styles.disabledButton,
                  ]}
                  icon='test-tube'
                  iconColor={_isSelectionModeOn ? styles.colors.gray : ((!!wrinkles.length && wrinkles.every((wrinkle) => (wrinkle.level_number || 0) >= globals.srsLevelWrinkleIsEarnedAt)) ? styles.colors.accent : (isTestOpen ? styles.colors.accentLight : styles.colors.accentLight))}
                  size={22 * styles.globalScale}
                  onPress={() => {
                    if (_isSelectionModeOn) {
                      return;
                    }
                    navigation.navigate(PRACTICE_ROUTE, {
                      passages: [props.passage], // @TODO: this is serialized as a pojo in web...need to make it a passage object again
                      isTest: true,
                      assignmentId: props.assignmentId,
                    });
                  }}
                />
                {(!_isSelectionModeOn && hasVersesReadyForLevelUp) &&
                  <View style={styles.fakeWrinkleBadge}>
                    {passageWrinkleLevelNumber >= 1 &&
                      <Text style={styles.levelText}>{passageWrinkleLevelNumber}</Text>
                    }
                  </View>
                }
              </View>
            }
            {!props.hideLessonIcon &&
              <IconButton
                style={[
                  styles.passageActionButton,
                  (!!wrinkles.length && wrinkles.every((wrinkle) => (wrinkle.level_number || 0) >= globals.srsLevelWrinkleIsEarnedAt)) && styles.levelFiveButton,
                  _isSelectionModeOn && styles.disabledButton,
                ]}
                icon='school'
                iconColor={_isSelectionModeOn ? styles.colors.gray : ((!!wrinkles.length && wrinkles.every((wrinkle) => (wrinkle.level_number || 0) >= globals.srsLevelWrinkleIsEarnedAt)) ? styles.colors.accent : styles.colors.accentLight)}
                size={22 * styles.globalScale}
                onPress={() => {
                  if (_isSelectionModeOn) {
                    return;
                  }
                  navigation.navigate(PRACTICE_ROUTE, {
                    passages: [props.passage],
                    difficultyOverride: props.difficultyOverride,
                    availableMinigamesOverride: props.availableMinigamesOverride,
                    isTest: false,
                    assignmentId: props.assignmentId,
                  });
                }}
              />
            }
          </View>
        }
      </View>
    </ListItem>
  );
};

class PassageProps {
  passage: CPassage;

  assignmentId?: number;
  normalizedScore?: number;
  isExpanded?: boolean;
  hideSelectionMenu?: boolean;
  hideActions?: boolean;
  hideLessonIcon?: boolean;
  hideTestIcon?: boolean;
  hideWrinkleBadges?: boolean;
  showHeadings?: boolean;
  difficultyOverride?: 'easy' | 'medium' | 'hard';
  availableMinigamesOverride?: number[];
  spritzPassage?: any;
  spritzGoToPreviousPassage?: () => void; // @TODO: verify signature
  spritzGoToNextPassage?: () => void; // @TODO: verify signature
  hideProgressStyling?: boolean;
  isPressDisabled?: boolean;
  includeTranslationAbbreviation?: boolean;
};

export default Passage;
